// Pure leave-hours calculation used by FormlyFieldDateHoursCalculator.
// Kept separate from the component so the logic is unit-testable without a render tree.
//
// Model: every working day (Mon–Fri) counts at most HOURS_PER_DAY. A whole-days request counts
// each working day in full; a timed request counts the hours the range covers on each day, where
// a day without an entered boundary runs from DAY_START to DAY_END. A contract caps the total per
// calendar week (Mon–Sun) at the contracted hours per week: someone on 32 hours who takes the
// whole week off takes 32 hours, not 40. Without contract hours a full-time week is assumed.
// TODO: this should ultimately be calculated in the backend, from the employee's actual roster.
const DAY_START_MIN = 8 * 60; // 08:00
const DAY_END_MIN = 17 * 60; // 17:00
const HOURS_PER_DAY = 8;
const DEFAULT_HOURS_PER_WEEK = 40;

// A date-only value (YYYY-MM-DD, no time component): the user picked a day without a time.
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

export interface LeaveHoursOptions {
  /** Count every working day in full and ignore any entered times. */
  wholeDays?: boolean;
  /** Contracted hours per week; caps the total of each calendar week. Unset or 0 = 40. */
  maxHoursPerWeek?: number | null;
}

interface Boundary {
  day: Date;
  /** Minutes since midnight, or null when only a date was entered. */
  minutes: number | null;
}

function toBoundary(value: string | Date): Boundary | null {
  if (typeof value === 'string' && DATE_ONLY.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return { day: new Date(y, m - 1, d), minutes: null };
  }
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) {
    return null;
  }
  return {
    day: new Date(date.getFullYear(), date.getMonth(), date.getDate()),
    minutes: date.getHours() * 60 + date.getMinutes(),
  };
}

/** Monday of the week `day` falls in, as a comparable key. */
function weekKey(day: Date): number {
  const monday = new Date(day);
  monday.setDate(day.getDate() - ((day.getDay() + 6) % 7));
  return monday.getTime();
}

/**
 * Leave hours between two boundaries. Weekends never count; each working day counts at most 8
 * hours; with `maxHoursPerWeek` each calendar week counts at most that many hours.
 *
 * Examples (Mon–Fri week): whole days Mon → Fri = 40h, or 32h with a 32-hour contract;
 * Mon 08:00 → Mon 12:00 = 4h; Mon 13:00 → Wed 12:00 = 4h + 8h + 4h = 16h.
 *
 * Returns 0 for invalid input (unparseable dates or end before start).
 */
export function calculateLeaveHours(
  startValue: string | Date,
  endValue: string | Date,
  options: LeaveHoursOptions = {}
): number {
  const start = toBoundary(startValue);
  const end = toBoundary(endValue);
  if (!start || !end || end.day.getTime() < start.day.getTime()) {
    return 0;
  }

  const perWeek = new Map<number, number>();
  for (let day = new Date(start.day); day.getTime() <= end.day.getTime(); day.setDate(day.getDate() + 1)) {
    const weekday = day.getDay();
    if (weekday === 0 || weekday === 6) {
      continue;
    }
    let hours = HOURS_PER_DAY;
    if (!options.wholeDays) {
      const from = day.getTime() === start.day.getTime() ? (start.minutes ?? DAY_START_MIN) : DAY_START_MIN;
      const to = day.getTime() === end.day.getTime() ? (end.minutes ?? DAY_END_MIN) : DAY_END_MIN;
      hours = Math.min(HOURS_PER_DAY, Math.max(0, to - from) / 60);
    }
    const key = weekKey(day);
    perWeek.set(key, (perWeek.get(key) ?? 0) + hours);
  }

  const cap = options.maxHoursPerWeek && options.maxHoursPerWeek > 0 ? options.maxHoursPerWeek : DEFAULT_HOURS_PER_WEEK;
  let total = 0;
  perWeek.forEach(hours => (total += Math.min(hours, cap)));
  return Math.round(total * 100) / 100;
}
